import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestFindActiveProgrammes,
  requestCreateMember,
  requestFlagMemberAsPaid,
  requestVerifyMemberStatus
} from './external-registration.actions';
import {
  getFindActiveProgrammesRequest,
  getCreateMemberRequest,
  getFlagMemberAsPaidRequest,
  getVerifyMemberStatusRequest
} from './external-registration.endpoints';
import {
  FINDING_ACTIVE_PROGRAMMES,
  CREATING_MEMBER,
  FLAGGING_MEMBER_AS_PAID,
  VERIFYING_MEMBER_STATUS
} from './external-registration.constants';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setProgrammes, setMemberStatus } from '../../slices/registration/registration.slice';

import { ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performFindActiveProgrammesRequest() {
  try {
    yield put(addSpinner(FINDING_ACTIVE_PROGRAMMES));

    const [endpoint, options] = getFindActiveProgrammesRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setProgrammes(data.programmes));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(FINDING_ACTIVE_PROGRAMMES));
  }
}

export function* watchForFindActiveProgrammesRequest() {
  yield takeLatest(requestFindActiveProgrammes.type, performFindActiveProgrammesRequest);
}

export function* performCreateMemberRequest({ payload }) {
  const { input, onSuccess } = payload;
  try {
    yield put(addSpinner(CREATING_MEMBER));

    const [endpoint, options] = getCreateMemberRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_MEMBER));
  }
}

export function* watchForCreateMemberRequest() {
  yield takeLatest(requestCreateMember.type, performCreateMemberRequest);
}

export function* performFlagMemberAsPaidRequest({ payload }) {
  const { memberExternalReference, programmeExternalReference } = payload;
  try {
    yield put(addSpinner(FLAGGING_MEMBER_AS_PAID));

    const [endpoint, options] = getFlagMemberAsPaidRequest(memberExternalReference, programmeExternalReference);

    yield call(axios, endpoint, options);
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(FLAGGING_MEMBER_AS_PAID));
  }
}

export function* watchForFlagMemberAsPaidRequest() {
  yield takeLatest(requestFlagMemberAsPaid.type, performFlagMemberAsPaidRequest);
}

export function* performVerifyMemberStatusRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner(VERIFYING_MEMBER_STATUS));

    const request = getVerifyMemberStatusRequest(externalReference);

    const { data } = yield call(axios, ...request);

    yield put(setMemberStatus(data.status));
  } catch (error) {
    const message = getErrorMessage(error);
    if (!message.includes('No members found')) {
      yield put(addSystemNotice({ type: ERROR, message }));
    }
  } finally {
    yield put(removeSpinner(VERIFYING_MEMBER_STATUS));
  }
}

export function* watchForVerifyMemberStatusRequest() {
  yield takeLatest(requestVerifyMemberStatus.type, performVerifyMemberStatusRequest);
}

export default function* externalRegistrationSaga() {
  yield all([
    watchForFindActiveProgrammesRequest(),
    watchForCreateMemberRequest(),
    watchForFlagMemberAsPaidRequest(),
    watchForVerifyMemberStatusRequest()
  ]);
}
